import {
  type AbstractControl,
  type AbstractControlOptions,
  type AsyncValidatorFn,
  FormArray,
  FormGroup,
  PristineChangeEvent,
  type ValidatorFn,
} from '@angular/forms';
import { type Signal, signal } from '@angular/core';
import { BehaviorSubject, filter, type Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { isFunction, isNil } from '@pct-ts/typeguard';
import { searchRootFormGroup } from './search-root-form-group';
import type { FormState } from './form-state';
import { FieldError, handleHttpError, type ResolvedFieldError } from '@pct-ng/core/error-http';
import { assertIsNotNil } from '@pct-ts/assert';
import type { FieldPath } from './field-error/field-path';
import type { FieldErrorOptions } from './field-error/field-error-options';
import { translateTranslateAbleText } from '@pct-ng/i18n';

export class PctFormGroup<
  CONTROL extends {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof CONTROL]: AbstractControl<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = any,
> extends FormGroup<CONTROL> {
  #fieldErrorsResolver: FieldErrorOptions | undefined = undefined;

  readonly #formState = new BehaviorSubject<FormState>('NOT_MODIFIED');
  get formState(): FormState {
    return this.#formState.getValue();
  }
  set formState(state: Exclude<FormState, 'NOT_SAVED'>) {
    this.#formState.next(state);
  }
  get formStateChanges(): Observable<FormState> {
    return this.#formState.asObservable();
  }

  readonly #submitted = signal(false);
  get submitted(): Signal<boolean> {
    if (!isNil(this.parent)) {
      const result = searchRootFormGroup(this)?.submitted;
      if (result === undefined) {
        throw new Error('[PctFormGroup::get submitted] Not found parent form submitted');
      }
      return result;
    }
    return this.#submitted.asReadonly();
  }

  get submitted$(): Observable<boolean> {
    if (!isNil(this.parent)) {
      const result = searchRootFormGroup(this)?.submitted$;
      if (result === undefined) {
        throw new Error('[PctFormGroup::get submitted] Not found parent form submitted');
      }
      return result;
    }
    return toObservable(this.#submitted);
  }

  constructor(
    controls: CONTROL,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
  ) {
    super(controls, validatorOrOpts, asyncValidator);

    this.events
      .pipe(filter(event => event instanceof PristineChangeEvent && event.pristine === false))
      .subscribe(() => this.#setNOT_SAVEDState());
  }

  registerFieldErrorsResolver<ERRORS>(options: FieldErrorOptions<ERRORS>): void {
    this.#fieldErrorsResolver = options;
  }

  setSubmitted(submitted = true, markAllAsTouched = true): void {
    if (markAllAsTouched) {
      this.markAllAsTouched();
    }
    this.#submitted.set(submitted);
  }

  handleHttpErrorsWithFieldErrors(afterHandler?: (error: unknown) => void) {
    return (error: any): void => {
      if (error instanceof FieldError) {
        assertIsNotNil(this.#fieldErrorsResolver, 'Field errors resolver is not set');
        const { resolver, i18nService, resolveNotFoundPath } = this.#fieldErrorsResolver;
        const unhandledError: ResolvedFieldError[] = [];

        const fieldErrors = resolver(error.fieldErrors);
        fieldErrors.forEach(({ path, msg }) => {
          const control = this.#getControlByPath(path);
          if (control !== null) {
            setTimeout(() => {
              control.markAsTouched();
              control.setErrors({
                remoteError: {
                  message: translateTranslateAbleText(`FIELD_ERROR.${msg}`, i18nService),
                },
              });
              control.root?.updateValueAndValidity();
            }, 100);
          } else {
            unhandledError.push({ path, msg });
            console.log('FieldError: control not found', path);
          }
        });
        if (isFunction(afterHandler)) {
          afterHandler(error);
        }
        if (unhandledError.length > 0) {
          resolveNotFoundPath(unhandledError);
        }
      } else {
        handleHttpError(afterHandler)(error);
      }
    };
  }

  #getControlByPath(path: string): AbstractControl | null {
    const pathSegments = this.#parseFieldPath(path);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let currentControl: AbstractControl | null = this;

    for (const segment of pathSegments) {
      if (segment.type === 'property') {
        if (currentControl instanceof FormGroup) {
          currentControl = currentControl.get(segment.name);
        } else {
          return null;
        }
      } else if (segment.type === 'array') {
        if (currentControl instanceof FormArray) {
          currentControl = currentControl.at(segment.index);
        } else {
          return null;
        }
      }

      if (currentControl === null) {
        return null;
      }
    }

    return currentControl;
  }

  #parseFieldPath(path: string): FieldPath[] {
    const segments: FieldPath[] = [];
    const parts = path.split('.');

    for (const part of parts) {
      const arrayMatch = part.match(/^([^[]+)\[(\d+)\]$/);

      if (arrayMatch) {
        // This is an array element, e.g.: "field2[1]"
        const [, fieldName, indexStr] = arrayMatch;
        segments.push({ type: 'property', name: fieldName });
        segments.push({ type: 'array', index: parseInt(indexStr, 10) });
      } else {
        // This is a simple property, e.g.: "field1" or "city"
        segments.push({ type: 'property', name: part });
      }
    }

    return segments;
  }

  #setNOT_SAVEDState(): void {
    this.#formState.next('NOT_SAVED');
  }
}
